import { useCallback, useEffect, useState } from 'react';
import { listCategories, type CategoryItem } from '../api/catalog';
import { ApiError } from '../api/httpClientFactory';

export interface CategoriesState {
  status: 'loading' | 'ready' | 'error';
  items: CategoryItem[];
  error: ApiError | null;
  reload: () => void;
}

function asApiError(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError('UNKNOWN', 0);
}

export function useCategories(): CategoriesState {
  const [status, setStatus] = useState<CategoriesState['status']>('loading');
  const [items, setItems] = useState<CategoryItem[]>([]);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [seenReload, setSeenReload] = useState(0);

  if (seenReload !== reloadToken) {
    setSeenReload(reloadToken);
    setStatus('loading');
    setError(null);
  }

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    listCategories(controller.signal).then(
      (next) => {
        if (!active) return;
        setItems(next);
        setError(null);
        setStatus('ready');
      },
      (reason: unknown) => {
        if (!active) return;
        if (reason instanceof ApiError && reason.code === 'SESSION_EXPIRED') return;
        setError(asApiError(reason));
        setStatus('error');
      },
    );
    return () => {
      active = false;
      controller.abort();
    };
  }, [reloadToken]);

  return { status, items, error, reload };
}
